import { DEFAULT_PAGE_INFO } from '@apeiron/common'
import { OrbitalTrackFilterInput, OrbitalTrackSortBy } from '@apeiron/orbital'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { orbitalTrackLiteSchema } from '@src/deserialize/yup/orbitalTrack'
import graphql from '@src/graphql/query/getOrbitalTracks'
import { OrbitalTrackLite } from '@src/types/orbitalTrack'
import { useCallback, useState } from 'react'

const useGetOrbitalTracks = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<OrbitalTrackLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const {
        filterInput = {},
        pageInput = {},
        sortInput = OrbitalTrackSortBy.StarOrbitalTrackSlotAsc
      } = request

      const response = await query({
        variables: {
          filterInput,
          pagingInput: serializePagingInput(pageInput),
          sortBy: sortInput
        }
      })

      const orbitalTracks = deserializeListData<OrbitalTrackLite>(
        response,
        orbitalTrackLiteSchema
      )

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(orbitalTracks)
      setPageInfo(pageInfo)

      return {
        data: orbitalTracks,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    loading,
    pageInfo,
    fetch
  }
}

type Request = {
  filterInput?: OrbitalTrackFilterInput
  pageInput?: PageInput
  sortInput?: OrbitalTrackSortBy
}

type Response = {
  data: OrbitalTrackLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: OrbitalTrackLite[]
  loading: boolean
  pageInfo: PageInfo
  fetch: (request: Request) => Promise<Response>
}

export default useGetOrbitalTracks
